import {Injectable} from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ConfigurationManager} from '../../data/manager/configuration/configuration.manager';
import {LocationManager} from '../../data/manager/location/location.manager';
import {CustomerManager} from '../../data/manager/customer/customer/customer.manager';
import {LocalStorageManager} from '../../data/manager/local-storage/local-storage.manager';
import {AlgoliaManager} from '../../data/manager/algolia/algolia.manager';
import {SessionStorageManager} from '../../data/manager/session-storage/session-storage.manager';
import { FavoritesManager } from 'src/app/data/manager/favorites/favorites.manager';
import { PrimeManager } from 'src/app/data/manager/prime/prime.manager';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.reducer';
import * as primeActions from 'src/app/data/actions/prime.actions';
import { OrderManager } from 'src/app/data/manager/order/order/order.manager';
import * as customTagActions from 'src/app/data/actions/custom-tag.actions';
import { iDataCreatePassword } from 'src/app/data/networking/models/customer/customer';
import { iResponseCreatePassword } from 'src/app/data/networking/models/customer/responses/customer-res';
import {BatchManager} from 'src/app/data/manager/batch/batch.manager';
import { IpRes } from 'src/app/data/networking/models/customer/responses/ip-res';
import { ExpressStoreRegistrationManager } from 'src/app/data/manager/express-store-registration/express-store-registration.manager';
import { FirebaseManager } from 'src/app/data/manager/firebase/firebase.manager';
import { TokenJwtResponse } from 'src/app/data/networking/models/shared/responses/token-jwt.response';
import { LoginBmcResponse } from 'src/app/data/networking/models/customer/responses/login-res';
import { ToastEvent } from 'src/app/ui/shared/event-listeners/toast/toast.event';
import {DeliveryTypeTimeResponse} from 'src/app/data/networking/models/delivery-type-time/delivery-type-time.model';
import { RecaptchaManager } from '../../data/manager/recaptcha/recaptcha.manager';

@Injectable({
  providedIn: 'root',
})
export class RootVm {
  constructor(
    private configManager: ConfigurationManager,
    private locationManager: LocationManager,
    private customerManager: CustomerManager,
    private lsm: LocalStorageManager,
    private ssm: SessionStorageManager,
    public algoliaManager: AlgoliaManager,
    private favorites: FavoritesManager,
    private primeManager : PrimeManager,
    private store: Store<AppState>,
    private orderManager: OrderManager,
    private batchManager: BatchManager,
    private expressStoreRegistrationManager: ExpressStoreRegistrationManager,
    private firebaseManager: FirebaseManager,
    private toastEvent: ToastEvent,
    private recaptchaManager: RecaptchaManager,
  ) { }

  validateServices(): Observable<any> {
    return this.configManager.validateServices();
  }

  getOriginProperties() {
    this.configManager.getOriginProperties().subscribe();
  }

  getGeneralConfig(): any {
    const generalConfig = this.ssm.get('multiCountry');
    return (generalConfig && generalConfig.general) ? generalConfig.general : {};
  }

  getSupportContact() {
    return this.locationManager.getSupportContact();
  }

  createAnonymous(): Observable<any> {
    if (this.lsm.has('customer')) {
      return of(null);
    }
    return this.customerManager.create(null, true);
  }

  hasCustomer(): Observable<any> {
    return this.customerManager.hasCustomer();
  }

  getLifeMilesConfig(): Observable<any> {
    return from(
      this.algoliaManager.search('', '', '', 'featureEnabled=0', false, 'properties'),
    )
      .pipe(map(res => {
        if (res && res.hits && res.hits.length > 0) {
          this.ssm.set('lifeMilesEnabled', res.hits[0].featureEnabled);
          return res.hits[0].featureEnabled;
        }
        return res;
      }));
  }

  getConfigurationMultiCountry(): any {
    if (this.ssm.has('multiCountry')) { return this.configManager.setEventCountryConfig(); }
    return this.configManager.getConfigurationMultiCountry();
  }

  getAvailability() {
    return this.locationManager.getActiveCities();
  }

  getSaGStatus() {
    return this.ssm.get('sagActive');
  }

  removeSaGStatus() {
    this.ssm.removeItem('sagActive');
  }

  setSaGStatus() {
    this.ssm.set('sagActive', true);
  }

  getCustomer() {
    return this.customerManager.customer;
  }

  getFavorites(): Observable<any> {
    return this.favorites.get();
  }

  createPassword(dataCreatePassword: iDataCreatePassword): Observable<iResponseCreatePassword> {
    const temporalCode = this.ssm.get('temporalCodeChangePass')

    dataCreatePassword = {
      ...dataCreatePassword,
      ...temporalCode,
    }

    return this.customerManager.createPassword(dataCreatePassword);
  }

  getLsCustomer() {
    return this.lsm.get('customer');
  }

  getPrimeConfig<T>(): Promise<T[]> {
    return this.algoliaManager.primeBrowse<T>();
  }

  setPrimeConfig(): void {
    this.getPrimeConfig().then(primeConfig => {
      this.lsm.set('primeConfig', primeConfig);
      this.store.dispatch(primeActions.getPrimeConfig({ config: primeConfig }));
    });
  }

  getUserOrders( data ): Observable<any> {
    return this.orderManager.getOrders( data );
  }

  saveFirstPurchaseOrders( data ) {
    this.lsm.set( 'firstPurchaseValidate', data )
  }

  getPropertyCustomTagProduct() : void{
    this.algoliaManager.customTagProduct().then(res => {
      if(res) {
        this.store.dispatch( customTagActions.getCustomTagConfiguration({config: [res]}));
      }
    });
  }

  getInternationalization() {
    this.configManager.getInternationalization();
  }

  logEvent(event: string, data: any) {
    return this.batchManager.logEvent(event, data);
  }

  validatetokens(): void {
    const customer = this.getLsCustomer();
    this.sessionExpiredMessage(customer);
    if(customer === undefined || customer.isAnonymous){ return; }
    const {tokensJwt} = customer;
    if(tokensJwt){
      this.customerManager.refreshTokensJwt(customer?.tokensJwt?.refreshToken).subscribe(
        (tokensJwt: TokenJwtResponse) => {
          this.customerManager.saveCustomer({...customer, tokensJwt});
        });
    } else {
      this.customerManager.loginBmc(customer).subscribe(
        (newtokens: LoginBmcResponse) => {
          this.customerManager.saveCustomer({...customer, ...newtokens});
        }
      );
    }
  } 

  sessionExpiredMessage(customer): void {
    const refreshTokenJWT = this.lsm.has('refreshTokenJWT')
    if(!customer?.customerId && refreshTokenJWT) {
      setTimeout(() => {
        const message = 'Su sesión ha expirado, por favor inicie sesión nuevamente';
        this.toastEvent.updateToast({error: true, message});        
        this.lsm.removeItem('refreshTokenJWT');
      }, 3000);
    }
  }


  getIpAddress() {
    this.expressStoreRegistrationManager.getIpAddress().subscribe({
      next: (res: IpRes) => {
        this.ssm.set('ipUser', res.ip);
      },
      error: (error) => {
        console.error('Failed to retrieve IP address:', error);
      }
    });
  }

  getInitialRemoteConfig(): void {
    this.firebaseManager.getInitialRemoteConfig();
  }

  getDeliveryTypeTimeFromService(): Observable<DeliveryTypeTimeResponse> {
    return this.orderManager.getDeliveryTypeTimeFromService();
  }

  actionsRecaptcha(actionValue: string): Promise<string> {
    return this.recaptchaManager.actionRecaptcha(actionValue);
  }
}
